// Packaging --- one rule for a PO's packaging lines on every PO screen: what the line's packaging part is, from its
// Category and printing (packagingOf(): "Printed box", "Bag with a label", "Plain bag"), and what the spec revision it
// goes to the vendor with lacks for printing (printGaps() over that revision's file kinds and text; a draft's is its item's current released
// revision, a sent line's the one it was sent with: useLineSpecs()). A sent line whose newer released revision has what
// it lacks says so and points to Send update (NewerSpecs, above the PO's tabs) instead of the spec. The Lines tab's
// Packaging band, the warning before sending, the compose dialog and the Documents tab all read usePoPacking().
// Product labels --- usePoLabels(): a line whose product's BOM carries HHL product labels, or a line that is one, with
// what's off about each label (labelContent()'s warnings, at the label's size); the warning before sending lists them
// once per product, never blocking (labels are made when the vendor downloads them, so a fix needs no update).
import { useMemo } from "react";
import { type BomIndex, type BomItem, resolve } from "../../../../shared/boms.ts";
import type { Item, Po, PoLine, SpecRevision } from "../../../../shared/db.gen.ts";
import { linesEditable } from "../../../../shared/po.ts";
import { isProductLabel, labelContent, labelSize, productLabelsFor, productsLabelled, sizeText } from "../../../../shared/product-labels.ts";
import { isPackagingPart, type Packaging, packagingOf, printGaps } from "../../../../shared/projects.ts";
import { SPEC_FILE_KIND_LABELS, type SpecFileKind } from "../../../../shared/specs.ts";
import { useBomStructure } from "../../lib/boms.ts";
import { can } from "../../lib/mutate.ts";
import { useRows } from "../../lib/sync.ts";
import { usePaneOpen } from "../../ui/SidePane.tsx";
import type { LineFile, LineSpec } from "./docs.ts";

/** The kinds of file a vendor prints packaging from. */
const PRINT_KINDS: ReadonlySet<string> = new Set(["dieline", "artwork"]);

export type LinePacking = {
  item: Item;
  pack: Packaging;
  /** What its revision lacks for the vendor to print it ("No released spec", "Dieline", "Print artwork"…); [] when nothing. */
  gaps: string[];
  /** The revision's dielines and artwork, in their released order. */
  printFiles: LineFile[];
  /**
   * A sent line's newer released revision that has everything `gaps` lacks: Send update gets it to the vendor (`update`:
   * it can now, the lines can change and you may send updates). null when there's none.
   */
  fixedBy: { revision: SpecRevision; update: boolean } | null;
};

/** A packaging part whose revision lacks what the vendor prints from: the warning before sending lists these. */
export type PrintGap = LinePacking & { lineId: string; sku: string };

export type PoPacking = {
  /** Packaging lines only, by line id. */
  byLine: Map<string, LinePacking>;
  /** Make POs --- a build's packaging parts (a box the CM buys and prints), by `${line id}:${part id}`. */
  byPart: Map<string, LinePacking>;
  /**
   * Each packaging part with gaps once (its first line), in line order: the warning before sending. Only while the
   * lines can change (a shipped PO's re-send has nothing to warn about: its lines keep their revisions).
   */
  gaps: PrintGap[];
};

/** A revision's text, English and Chinese together: what text printing prints (printGaps()' `words`); null without one. */
const wordsOf = (r: SpecRevision | null | undefined) => (r ? [r.body_md, r.body_md_zh].join("\n") : null);

export function usePoPacking(po: Po | undefined, lines: PoLine[], specs: Map<string, LineSpec>): PoPacking {
  const items = useRows("item");
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const status = po?.status;
  return useMemo(() => {
    const byLine = new Map<string, LinePacking>();
    const byPart = new Map<string, LinePacking>();
    const gaps: PrintGap[] = [];
    const seen = new Set<string>();
    const editable = !!status && linesEditable(status);
    const packing = (item: Item, pack: Packaging, spec: Omit<LineSpec, "parts"> | undefined): LinePacking => {
      const lacks = printGaps(item, spec?.kinds ?? null, wordsOf(spec?.revision)) ?? [];
      const newer = lacks.length ? spec?.newer : null;
      return {
        item,
        pack,
        gaps: lacks,
        printFiles: (spec?.files ?? []).filter((f) => PRINT_KINDS.has(f.kind)),
        fixedBy:
          newer && printGaps(item, spec?.newerKinds ?? null, wordsOf(newer))?.length === 0
            ? { revision: newer, update: editable && can("po.send_spec_update") }
            : null,
      };
    };
    const gap = (p: LinePacking, lineId: string, sku: string) => {
      if (!editable || !p.gaps.length || seen.has(p.item.id)) return;
      seen.add(p.item.id);
      gaps.push({ ...p, lineId, sku });
    };
    for (const l of [...lines].sort((a, b) => a.position - b.position)) {
      const item = itemById.get(l.item_id);
      const spec = specs.get(l.id);
      const pack = item ? packagingOf(item) : null;
      if (item && pack) {
        const p = packing(item, pack, spec);
        byLine.set(l.id, p);
        gap(p, l.id, l.sku || item.sku || l.name);
      }
      // Make POs --- a build's packaging parts, printed by the CM from what this PO gives it.
      for (const part of spec?.parts ?? []) {
        const partItem = itemById.get(part.itemId);
        const partPack = partItem ? packagingOf(partItem) : null;
        if (!partItem || !partPack) continue;
        const p = packing(partItem, partPack, part);
        byPart.set(`${l.id}:${partItem.id}`, p);
        gap(p, l.id, `${partItem.sku || partItem.name} (in ${l.sku || item?.sku || l.name})`);
      }
    }
    return { byLine, byPart, gaps };
  }, [status, lines, specs, itemById]);
}

type Size = { w: number; h: number };

/** Product labels --- a label part on a product: its size and what's off about the product's label on it. */
export type ProductLabelOf = { item: Item; size: Size | null; warnings: string[] };

/**
 * Product labels --- a line's labels: `part` when its item is an HHL product label (its size, how many products it
 * labels, a page each in its PDF, why the vendor gets none and what's off about the labels the vendor gets), `product`
 * when its item's BOM puts HHL product labels in it (each label part with its size and what's off; the vendor that makes
 * it downloads them from the documents page). `warnings` are the line's, once each.
 */
export type LineLabels =
  | {
      kind: "part";
      item: Item;
      size: Size | null;
      /** The products it labels (drafts' too: the staff PDF). */
      products: number;
      /** Why the vendor gets no labels from it ("Not on any product's BOM"), else null. */
      gap: string | null;
      /** The products the vendor gets labels for (released BOMs) whose label has something off. */
      off: { product: Item; warnings: string[] }[];
      /** `gap`, then each of `off` as "GCU1400: label barcode is the SKU (no UPC in Shopify)". */
      warnings: string[];
    }
  | { kind: "product"; labels: ProductLabelOf[]; warnings: string[] };

/** Product labels --- a product whose label has something off, or a label part the vendor gets no labels from. */
export type LabelGap = {
  /** The product's id (or the label part's, for its own gap): each is listed once. */
  key: string;
  sku: string;
  /** "label barcode is the SKU (no UPC in Shopify)", "not on any product's BOM". */
  text: string;
  /** The label part, opened to fix it: its Packaging section lists each product's label with what's off. */
  label: Item;
};

export type PoLabels = {
  byLine: Map<string, LineLabels>;
  /** Only while the lines can change, like the print-file gaps: the warning before sending. */
  gaps: LabelGap[];
};

/**
 * Product labels --- the HHL product labels a PO line's product carries: a build on a make PO keeps the BOM revision its
 * make order was made with (`bomId`, make_order.bom_id, as the server does), any other line its product's current BOM.
 */
export function labelsOn(ix: BomIndex, item: BomItem, bomId: string | null | undefined): BomItem[] {
  const bom = bomId ? ix.bomById.get(bomId) : undefined;
  if (!bom) return productLabelsFor(ix, item);
  const out = new Map<string, BomItem>();
  for (const l of resolve(ix, bom, bom.shopify_product_id ? item : undefined).lines) if (isProductLabel(l.item)) out.set(l.item.id, l.item);
  return [...out.values()];
}

/**
 * A label warning (labelContent()) worded to follow a product's SKU: "GCU1400: label barcode is the SKU (no UPC in
 * Shopify)". Its sentences reworded; anything else as it is.
 */
const SHORT: [RegExp, string][] = [
  [/^No UPC in Shopify: the barcode is the SKU$/, "label barcode is the SKU (no UPC in Shopify)"],
  [/^No UPC or SKU: no barcode$/, "no barcode on its label (no UPC or SKU)"],
  [/^Its barcode (.+) isn't a UPC or EAN \((.+)\): printed as Code 128$/, "label barcode $1 isn't a UPC or EAN ($2)"],
  [/^Its barcode is too narrow to scan on (.+?) \((.+?) bars\).*$/, "label barcode too narrow to scan on $1 ($2 bars)"],
  [/^“(.*)” has characters a barcode can't hold: printed as “(.*)”$/, "label barcode leaves out what it can't hold (“$1” printed as “$2”)"],
  [/^No country of origin: no “Made in” line$/, "no “Made in” on its label (no country of origin)"],
];
export function shortWarning(w: string): string {
  for (const [re, to] of SHORT) if (re.test(w)) return w.replace(re, to);
  return w.charAt(0).toLowerCase() + w.slice(1);
}

function labelsOfLine(ix: BomIndex, item: Item, bomId: string | undefined, itemById: Map<string, Item>): LineLabels | null {
  if (isProductLabel(item)) {
    const all = productsLabelled(ix, item.id);
    // What the vendor gets: products whose BOM in use is released (a draft-only product isn't announced yet).
    const released = all.length ? productsLabelled(ix, item.id, { released: true }) : [];
    const off = released.flatMap((p) => {
      const product = itemById.get(p.id);
      const warnings = product ? labelContent(product, item).warnings : [];
      return product && warnings.length ? [{ product, warnings }] : [];
    });
    const gap = released.length ? null : all.length ? "Only on draft BOMs (vendors get its labels once one is released)" : "Not on any product's BOM";
    const warnings = [...(gap ? [gap] : []), ...off.map((o) => `${o.product.sku || o.product.name}: ${o.warnings.map(shortWarning).join(", ")}`)];
    return { kind: "part", item, size: labelSize(item), products: all.length, gap, off, warnings };
  }
  if (isPackagingPart(item)) return null; // packaging parts have no BOM to look in
  const labels = labelsOn(ix, item, bomId).flatMap((b) => {
    const part = itemById.get(b.id);
    return part ? [{ item: part, size: labelSize(part), warnings: labelContent(item, part).warnings }] : [];
  });
  return labels.length ? { kind: "product", labels, warnings: [...new Set(labels.flatMap((x) => x.warnings))] } : null;
}

export function usePoLabels(po: Po | undefined, lines: PoLine[]): PoLabels {
  const ix = useBomStructure(); // no stock: an inventory change doesn't walk the BOMs again
  const items = useRows("item"); // whole rows: labelContent() reads the barcode, country and titles
  const makeOrders = useRows("make_order");
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const poId = po?.id;
  const status = po?.status;
  // A build's BOM revision (its make order's), by line.
  const pinned = useMemo(
    () => new Map(makeOrders.flatMap((m) => (m.po_id === poId && m.bom_id ? [[m.po_line_id, m.bom_id] as const] : []))),
    [makeOrders, poId],
  );
  return useMemo(() => {
    const byLine = new Map<string, LineLabels>();
    const gaps: LabelGap[] = [];
    const editable = !!status && linesEditable(status);
    const listed = new Set<string>();
    const gap = (key: string, sku: string, texts: string[], label: Item) => {
      if (!editable || !texts.length || listed.has(key)) return;
      listed.add(key);
      gaps.push({ key, sku, text: texts.join(", "), label });
    };
    const done = new Map<string, LineLabels | null>(); // an item on two lines (with the same BOM) is worked out once
    for (const l of [...lines].sort((a, b) => a.position - b.position)) {
      const item = itemById.get(l.item_id);
      if (!item) continue;
      const bomId = pinned.get(l.id);
      const key = `${item.id}:${bomId ?? ""}`;
      let labels = done.get(key);
      if (labels === undefined) {
        labels = labelsOfLine(ix, item, bomId, itemById);
        done.set(key, labels);
      }
      if (!labels) continue;
      byLine.set(l.id, labels);
      const sku = l.sku || item.sku || l.name;
      if (labels.kind === "product") {
        const first = labels.labels.find((x) => x.warnings.length);
        if (first) gap(item.id, sku, labels.warnings.map(shortWarning), first.item);
      } else {
        if (labels.gap) gap(item.id, sku, [labels.gap.charAt(0).toLowerCase() + labels.gap.slice(1)], item);
        for (const o of labels.off) gap(o.product.id, o.product.sku || o.product.name, o.warnings.map(shortWarning), item);
      }
    }
    return { byLine, gaps };
  }, [status, lines, ix, itemById, pinned]);
}

/** Product labels --- what clicking a label warning does, for its title. */
export const LABEL_FIX_TITLE = "Open the label part: it lists each product's label with what's off about it";
/** Opens the label part, in the side pane. */
export function useOpenLabel() {
  const open = usePaneOpen();
  return (g: Pick<LabelGap, "label">) => open("item", g.label.id);
}
/** Why sending with a label warning needs no update later. */
export const LABELS_LATER = "labels are made when the vendor downloads them, so a fix reaches the vendor without sending again.";

/**
 * Product labels --- the open POs already sent whose vendor downloads labels from this label part (labels are made
 * when downloaded, so a change to it changes theirs): a line that is the part, or whose product carries it (a build by
 * its make order's BOM revision).
 */
export function useSentPosLabelling(labelId: string): Po[] {
  const ix = useBomStructure();
  const pos = useRows("po");
  const poLines = useRows("po_line");
  const makeOrders = useRows("make_order");
  return useMemo(() => {
    const open = new Map(pos.filter((p) => p.sent_revision !== null && linesEditable(p.status)).map((p) => [p.id, p]));
    if (!open.size) return [];
    const labelled = new Set(productsLabelled(ix, labelId).map((p) => p.id));
    const pinned = new Map(makeOrders.flatMap((m) => (m.bom_id && open.has(m.po_id) ? [[m.po_line_id, m.bom_id] as const] : [])));
    const found = new Set<string>();
    for (const l of poLines) {
      if (!open.has(l.po_id) || found.has(l.po_id)) continue;
      const bomId = pinned.get(l.id);
      const item = bomId ? ix.itemById.get(l.item_id) : undefined;
      const carries = l.item_id === labelId || (item && bomId ? labelsOn(ix, item, bomId).some((x) => x.id === labelId) : labelled.has(l.item_id));
      if (carries) found.add(l.po_id);
    }
    return [...found].flatMap((id) => open.get(id) ?? []).sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));
  }, [ix, pos, poLines, makeOrders, labelId]);
}

/** "100 × 150 mm", "size not set": a label's size for titles. */
export const sizeNote = (s: Size | null) => (s ? sizeText(s) : "size not set");

/** A print file's short name: the words the gaps use. */
export const PRINT_FILE_LABELS: Record<string, string> = { dieline: "Dieline", artwork: "Print artwork" };

/**
 * Product labels --- the files an HHL product label's released revision still has from before it was one (a label
 * printed from artwork): "Print artwork", "Dieline and Print artwork"; '' when none. The vendor's page leaves a label
 * part's files out, so vendors don't get them any more (they get the labels HHL Ops makes).
 */
export function leftoverFiles(kinds: Iterable<string>): { text: string; many: boolean } {
  const names = [...new Set(kinds)].map((k) => PRINT_FILE_LABELS[k] ?? SPEC_FILE_KIND_LABELS[k as SpecFileKind] ?? k);
  const text = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : (names[0] ?? "");
  return { text, many: names.length > 1 };
}
/** The same as a sentence for a title: ". Its Print artwork isn't sent: vendors get the labels HHL Ops makes instead". */
export function leftoverNote(kinds: Iterable<string>): string {
  const files = leftoverFiles(kinds);
  return files.text ? `. Its ${files.text} ${files.many ? "aren't" : "isn't"} sent: vendors get the labels HHL Ops makes instead` : "";
}

/** Why a packaging line has nothing to download, when that's as it should be (pack.says has the whole sentence); null when it needs print files. */
export function noFilesNote(pack: Packaging): string | null {
  if (pack.files.length || !pack.type) return null;
  if (pack.print === "template") return "HHL Ops makes its labels"; // Product labels ---
  return pack.print === "text" ? "Words in its spec" : "No files needed";
}

type Gap = Pick<LinePacking, "pack" | "gaps" | "fixedBy">;

/**
 * "no Dieline, no Print artwork", "no released spec", "Category not set"; with a newer revision that has it,
 * "no Print artwork (Rev B has it: Send update)".
 */
export function gapText(p: Gap): string {
  const text = p.gaps.map((g) => (p.pack.files.some((f) => f.label === g) ? `no ${g}` : g.charAt(0).toLowerCase() + g.slice(1))).join(", ");
  if (!p.fixedBy) return text;
  const has = p.gaps.includes("No released spec") ? "is released" : p.gaps.length > 1 || p.gaps[0] === "No words to print" ? "has them" : "has it";
  return `${text} (Rev ${p.fixedBy.revision.rev} ${has}${p.fixedBy.update ? ": Send update" : ""})`;
}

/** The same, starting a sentence: "No Dieline, no Print artwork". */
export const gapSentence = (p: Gap) => {
  const t = gapText(p);
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/** Send update is the banner above the PO's tabs (NewerSpecs): bring it into view and light it up for a moment. */
function pointToSendUpdate() {
  const button = document.querySelector<HTMLElement>("[data-send-update]");
  if (!button) return;
  button.scrollIntoView({ block: "nearest" });
  button.focus();
  const warn = getComputedStyle(button).getPropertyValue("--warn").trim() || "orange";
  button.animate([{ boxShadow: `0 0 0 4px ${warn}` }, { boxShadow: "0 0 0 0 transparent" }], { duration: 1200, easing: "ease-out" });
}

/**
 * Where a gap is fixed: Send update when a newer revision already has it, the part itself while its Category (or an HHL
 * product label's size) isn't set, else its spec, both opened in the side pane beside the PO.
 */
export function useOpenFix() {
  const open = usePaneOpen();
  return (p: Pick<LinePacking, "item" | "pack" | "fixedBy">) => {
    if (p.fixedBy?.update) pointToSendUpdate();
    else open(!p.pack.type || p.pack.print === "template" ? "item" : "spec", p.item.id);
  };
}

/** What clicking a gap does, for its title. */
export function fixTitle(p: Pick<LinePacking, "pack" | "fixedBy">) {
  const f = p.fixedBy;
  if (f?.update) return `Rev ${f.revision.rev} was released after this PO was sent: Send update (above) moves the line to it`;
  if (!p.pack.type) return "Open the part to set its Category";
  if (p.pack.print === "template") return "Open the label part to set its size"; // Product labels ---
  if (f) return `Rev ${f.revision.rev} was released after this PO was sent; this line keeps the revision it was sent with. Open its spec`;
  if (p.pack.print !== "text") return "Open its spec: add the print files and release it";
  return p.pack.files.length
    ? "Open its spec: add the print files, write the words to print and release it"
    : "Open its spec: write the words to print and release it";
}
